// Paso "Confirmación" del formulario v2: resumen de lo diligenciado (con "Editar" para volver a
// cada paso) y las casillas de aceptación. De la caracterización solo se muestra cuántas
// preguntas respondió, para no exponer datos sensibles en pantalla. Las aceptaciones y el error
// del último envío viven en InscripcionAgregar.
import React, { useEffect, useRef, useState } from "react";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import AssignmentTurnedInOutlinedIcon from "@material-ui/icons/AssignmentTurnedInOutlined";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { useEstilosInscripcion } from "./estilosInscripcion";
import { Seccion } from "./componentesInscripcion";
import { PASOS_CARACTERIZACION, PRIMER_PASO_CARACTERIZACION, preguntasDelPaso } from "./caracterizacion";
import { campoAceptacion } from "./confirmacion";

const etiqueta = (valor) => (valor && valor.label) || "";
const unir = (...partes) => partes.filter(Boolean).join(" · ");

// "Declaración de veracidad y autorización" del formato de inscripción 2027 (Parte 2), tal cual.
// La casilla de datos sensibles lleva su resumen y este texto se despliega con "Leer la declaración completa"
const DECLARACION_COMPLETA = [
  "Declaro que la información suministrada en este formulario es completa, veraz y corresponde a mi situación actual.",
  "Entiendo que esta caracterización tiene como finalidad permitir a la institución identificar necesidades de acompañamiento, accesibilidad, inclusión, bienestar y permanencia estudiantil, con el propósito de brindar los apoyos y ajustes razonables requeridos para favorecer mi proceso formativo.",
  "Comprendo que la omisión, ocultamiento o suministro inexacto de información relevante puede limitar la capacidad institucional para activar oportunamente las rutas de atención, los apoyos especializados o los ajustes requeridos para mi adecuado desempeño académico.",
  "Asimismo, autorizo a la institución para solicitar aclaraciones, documentación de soporte o realizar las remisiones correspondientes a las áreas académicas, de bienestar o inclusión cuando se identifiquen necesidades específicas de acompañamiento.",
];

// Texto de cada casilla
function textoAceptacion(clave, linkPolitica, linkConsentimiento) {
  const enlace = (href, texto) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{texto}</a>
  );
  switch (clave) {
    case "politica":
      return <React.Fragment>Acepto las {enlace(linkPolitica, "políticas de protección de datos")} de la Universidad de la Costa.</React.Fragment>;
    case "consentimiento":
      return <React.Fragment>Acepto los términos del {enlace(linkConsentimiento, "consentimiento informado")} para la realización de la prueba psicotécnica del programa de Psicología.</React.Fragment>;
    case "datos_sensibles":
      return <React.Fragment><strong>Declaro</strong> que la información que suministré en la caracterización es completa, veraz y corresponde a mi situación actual, y entiendo que omitirla o darla de forma inexacta puede limitar los apoyos y ajustes razonables que la institución pueda brindarme. <strong>Autorizo</strong> de manera expresa el tratamiento de mis datos sensibles (salud, discapacidad y bienestar) con fines de acompañamiento, accesibilidad, inclusión, bienestar y permanencia, así como la solicitud de aclaraciones o soportes y las remisiones a las áreas académicas, de bienestar o inclusión cuando se requiera.</React.Fragment>;
    default:
      return null;
  }
}

export default function PasoConfirmacion({ props_c, irAPaso }) {
  const classes = useEstilosInscripcion();
  const {
    preguntas_caracterizacion = [], respuestas_caracterizacion = {}, aceptaciones = {}, aceptaciones_requeridas = [],
    setAceptacion, campo_error, error_envio, link_politica, link_consentimiento_informado,
  } = props_c;

  const [verDeclaracion, setVerDeclaracion] = useState(false);

  // Si el envío falla, el mensaje queda en pantalla y la vista baja hasta él
  const refError = useRef(null);
  useEffect(() => {
    if (error_envio && refError.current) refError.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error_envio]);

  const respondidas = (paso) => {
    const visibles = preguntasDelPaso(preguntas_caracterizacion, respuestas_caracterizacion, paso);
    const con_respuesta = visibles.filter((p) => {
      const r = respuestas_caracterizacion[p.codigo] || {};
      return (r.opciones || []).length || String(r.texto || "").trim();
    });
    return `${con_respuesta.length} de ${visibles.length} preguntas respondidas`;
  };

  const nombre = [props_c.primer_nombre, props_c.segundo_nombre, props_c.primer_apellido, props_c.segundo_apellido].filter(Boolean).join(" ");
  const resumen = [
    { paso: 0, titulo: "Mis datos", datos: [nombre, unir(etiqueta(props_c.tipo_identificacion), props_c.identificacion), unir(props_c.celular1, props_c.correo)] },
    { paso: 1, titulo: "Mi contacto", datos: [unir(props_c.direccion, props_c.barrio), unir(etiqueta(props_c.ciudad), etiqueta(props_c.departamento))] },
    { paso: 2, titulo: "Mi carrera", datos: [etiqueta(props_c.carrera), etiqueta(props_c.sede)] },
    ...(preguntas_caracterizacion.length
      ? PASOS_CARACTERIZACION.map((c, i) => ({ paso: PRIMER_PASO_CARACTERIZACION + i, titulo: c.nombre, datos: [respondidas(c.paso)] }))
      : []),
  ];

  return (
    <React.Fragment>
      <p className={classes.tituloPaso}>Revisa y confirma</p>
      <p className={classes.descripcionPaso}>
        Verifica que tus datos estén correctos antes de enviar. Si algo no está bien, usa "Editar" para volver a ese paso.
      </p>

      {error_envio && (
        <div ref={refError} className={classes.envioError} role="alert">
          <ErrorOutlineIcon className={classes.envioErrorIcono} />
          <div>
            <p className={classes.envioErrorTitulo}>No se pudo enviar tu inscripción</p>
            <p>{error_envio}</p>
          </div>
        </div>
      )}

      <Seccion icono={AssignmentTurnedInOutlinedIcon} titulo="Resumen de tu inscripción">
        <Grid item xs={12}>
          {resumen.map((r) => (
            <div key={r.paso} className={classes.resumenFila}>
              <div>
                <p className={classes.resumenPaso}>{r.titulo}</p>
                {r.datos.filter(Boolean).map((dato, i) => <p key={i} className={classes.resumenDato}>{dato}</p>)}
              </div>
              <Button size="small" className={classes.resumenEditar} startIcon={<EditOutlinedIcon fontSize="small" />} onClick={() => irAPaso(r.paso)}>
                Editar
              </Button>
            </div>
          ))}
        </Grid>
      </Seccion>

      <Seccion icono={VerifiedUserOutlinedIcon} titulo="Autorizaciones">
        {aceptaciones_requeridas.map(({ clave }) => {
          const marcada = !!aceptaciones[clave];
          const error = campo_error === campoAceptacion(clave);
          return (
            <Grid item xs={12} key={clave} {...(error ? { "data-error": "true" } : {})}>
              <label className={`${classes.aceptacion} ${marcada ? classes.aceptacionMarcada : ""} ${error ? classes.aceptacionError : ""}`}>
                <Checkbox checked={marcada} onChange={(e) => setAceptacion(clave, e.target.checked)} size="small" />
                <span>{textoAceptacion(clave, link_politica, link_consentimiento_informado)} *</span>
              </label>
              {/* Fuera de la casilla para que abrir el texto no la marque */}
              {clave === "datos_sensibles" && (
                <React.Fragment>
                  <button type="button" className={classes.declaracionEnlace} onClick={() => setVerDeclaracion(!verDeclaracion)} aria-expanded={verDeclaracion}>
                    {verDeclaracion ? "Ocultar la declaración completa" : "Leer la declaración completa"}
                  </button>
                  {verDeclaracion && (
                    <div className={classes.declaracionCompleta}>
                      <p className={classes.declaracionTitulo}>Declaración de veracidad y autorización</p>
                      {DECLARACION_COMPLETA.map((texto, i) => <p key={i}>{texto}</p>)}
                    </div>
                  )}
                </React.Fragment>
              )}
            </Grid>
          );
        })}
      </Seccion>
    </React.Fragment>
  );
}
